import { useState } from "react"
import { ChevronLeft, ChevronRight, Loader2, MessageSquare } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import {
  useConversationList,
  useConversationReply,
  useConversationThread,
  type AdminConversation,
} from "@/lib/queries/conversations"

// PAGE: Questions — admin reply dashboard for V-O question threads.
// Lets admins answer questions asked to ANY coach, including admin-
// created coaches (Poetry Collection, Goodreads) with no owner.

function fmt(ts?: string | null): string {
  return ts ? new Date(ts).toLocaleString() : "—"
}

export default function Conversations() {
  const [page, setPage] = useState(1)
  const limit = 20
  const listQ = useConversationList({ page, limit })

  const [active, setActive] = useState<AdminConversation | null>(null)
  const threadQ = useConversationThread(active?._id)
  const replyMut = useConversationReply()
  const [reply, setReply] = useState("")

  const totalPages = listQ.data?.totalPages ?? 0

  const sendReply = async () => {
    if (!active || !reply.trim()) return
    await replyMut.mutateAsync({
      conversation_id: active._id,
      kind: "text",
      body: reply.trim(),
    })
    setReply("")
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Questions</CardTitle>
          <CardDescription>
            Answer questions asked to any coach — including admin-created
            coaches that aren't linked to a user. Replies post as the coach.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Coach</TableHead>
                <TableHead>Asked by</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Messages</TableHead>
                <TableHead>Last message</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {listQ.isLoading && (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </TableCell>
                </TableRow>
              )}
              {!listQ.isLoading && (listQ.data?.docs.length ?? 0) === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                    No questions yet.
                  </TableCell>
                </TableRow>
              )}
              {listQ.data?.docs.map((c) => (
                <TableRow key={c._id}>
                  <TableCell className="font-medium">
                    {c.coach?.name || "(unknown coach)"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {c.user?.name || c.user?.email || "(unknown)"}
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        "rounded-full px-2 py-0.5 text-xs font-medium",
                        c.awaiting_coach
                          ? "bg-amber-100 text-amber-800"
                          : "bg-muted text-muted-foreground"
                      )}
                    >
                      {c.awaiting_coach ? "Awaiting reply" : "Replied"}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {c.message_count ?? 0}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {fmt(c.last_message_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setReply("")
                        setActive(c)
                      }}
                    >
                      <MessageSquare className="mr-1 h-4 w-4" />
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
        <div className="flex items-center justify-between border-t px-4 py-3 text-sm text-muted-foreground">
          <span>
            {listQ.data?.totalDocs ?? 0} total
          </span>
          <div className="flex items-center gap-3">
            <span>
              Page {page}
              {totalPages > 0 ? ` of ${totalPages}` : ""}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={totalPages > 0 && page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Dialog open={!!active} onOpenChange={(open) => !open && setActive(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {active?.coach?.name || "Conversation"}
            </DialogTitle>
            <DialogDescription>
              Asked by {active?.user?.name || active?.user?.email || "a user"}.
              Your reply posts as the coach.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-72 space-y-2 overflow-y-auto rounded-md border bg-muted/30 p-3">
            {threadQ.isLoading && (
              <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
            )}
            {threadQ.data?.messages.map((m) => (
              <div
                key={m._id}
                className={cn(
                  "max-w-[85%] rounded-lg px-3 py-2 text-sm",
                  m.sender === "coach"
                    ? "ml-auto bg-primary text-primary-foreground"
                    : m.sender === "system"
                      ? "mx-auto bg-transparent text-center text-xs text-muted-foreground"
                      : "mr-auto bg-background border"
                )}
              >
                {m.kind === "voice" ? (
                  <span className="italic">🎙 Voice message</span>
                ) : (
                  m.body || ""
                )}
                {m.sender !== "system" && (
                  <div className="mt-1 text-[10px] opacity-60">{fmt(m.sent_at)}</div>
                )}
              </div>
            ))}
            {!threadQ.isLoading && (threadQ.data?.messages.length ?? 0) === 0 && (
              <p className="text-center text-sm text-muted-foreground">
                No messages.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Textarea
              rows={3}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Write a reply as the coach…"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setActive(null)}
              disabled={replyMut.isPending}
            >
              Close
            </Button>
            <Button
              onClick={sendReply}
              disabled={replyMut.isPending || !reply.trim()}
            >
              {replyMut.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Send reply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
